import { lumi } from '@/lib/lumi'
import type { StaffRole } from '@/types/author'

const SESSION_KEY = 'bonita_admin_session'
const SESSION_USER_KEY = 'bonita_admin_user'
const SESSION_ROLE_KEY = 'bonita_admin_role'
const SESSION_FULL_NAME_KEY = 'bonita_admin_full_name'
const SESSION_HOST_ID_KEY = 'bonita_host_id'

export const BUILTIN_ADMIN = {
  username: 'Admin',
  password: 'Admin200%',
} as const

function persistSession(username: string, role: StaffRole, fullName: string, hostId = '') {
  window.localStorage.setItem(SESSION_HOST_ID_KEY, hostId)
  window.localStorage.setItem(SESSION_KEY, '1')
  window.localStorage.setItem(SESSION_USER_KEY, username)
  window.localStorage.setItem(SESSION_ROLE_KEY, role)
  window.localStorage.setItem(SESSION_FULL_NAME_KEY, fullName)
}

export function isAdminSession(): boolean {
  try {
    return window.localStorage.getItem(SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export function getAdminName(): string {
  try {
    return window.localStorage.getItem(SESSION_USER_KEY) || BUILTIN_ADMIN.username
  } catch {
    return BUILTIN_ADMIN.username
  }
}

export function getStaffRole(): StaffRole {
  try {
    const role = window.localStorage.getItem(SESSION_ROLE_KEY)
    if (role === 'author') return 'author'
    if (role === 'host') return 'host'
    return 'admin'
  } catch {
    return 'admin'
  }
}

export function getHostId(): string {
  try {
    return window.localStorage.getItem(SESSION_HOST_ID_KEY) || ''
  } catch {
    return ''
  }
}

export function getStaffFullName(): string {
  try {
    return window.localStorage.getItem(SESSION_FULL_NAME_KEY) || getAdminName()
  } catch {
    return getAdminName()
  }
}

function normalizeName(value?: string | null) {
  return (value || '').trim().toLowerCase()
}

async function findHostId(username: string, fullName: string): Promise<string> {
  try {
    const { list } = await lumi.entities.hosts.list({ limit: 200 })
    const hosts = (list || []) as { _id?: string; username?: string; fullName?: string }[]
    const byUser = hosts.find((item) => normalizeName(item.username) === normalizeName(username))
    if (byUser?._id) return byUser._id
    const byName = hosts.find((item) => normalizeName(item.fullName) === normalizeName(fullName))
    return byName?._id || ''
  } catch (error) {
    console.error('Falha ao ligar conta de locutor:', error)
    return ''
  }
}

export async function loginAdmin(username: string, password: string): Promise<boolean> {
  const user = username.trim()
  if (user === BUILTIN_ADMIN.username && password === BUILTIN_ADMIN.password) {
    persistSession(BUILTIN_ADMIN.username, 'admin', 'Administrador')
    return true
  }

  try {
    const { list } = await lumi.entities.authors.list({
      filter: { username: user },
      limit: 20,
    })
    const match = (list || []).find((item: { username?: string; password?: string; active?: boolean; fullName?: string }) => {
      const sameUser = (item?.username || '').trim().toLowerCase() === user.toLowerCase()
      const samePass = item?.password === password
      const isActive = item?.active !== false
      return sameUser && samePass && isActive
    })
    if (match) {
      // Autor que também é locutor: liga a conta ao painel do locutor dele.
      const hostId = await findHostId(match.username || user, match.fullName || '')
      persistSession(match.username || user, 'author', match.fullName || user, hostId)
      return true
    }
  } catch (error) {
    console.error('Falha ao validar autor:', error)
  }

  try {
    const { list } = await lumi.entities.hosts.list({
      filter: { username: user },
      limit: 20,
    })
    const host = (list || []).find((item: { username?: string; password?: string; active?: boolean }) => {
      const sameUser = (item?.username || '').trim().toLowerCase() === user.toLowerCase()
      return sameUser && item?.password === password && item?.active !== false
    }) as { _id?: string; username?: string; fullName?: string } | undefined
    if (!host || !host._id) return false
    persistSession(host.username || user, 'host', host.fullName || user, host._id)
    return true
  } catch (error) {
    console.error('Falha ao validar locutor:', error)
    return false
  }
}

export function logoutAdmin(): void {
  window.localStorage.removeItem(SESSION_KEY)
  window.localStorage.removeItem(SESSION_USER_KEY)
  window.localStorage.removeItem(SESSION_ROLE_KEY)
  window.localStorage.removeItem(SESSION_FULL_NAME_KEY)
  window.localStorage.removeItem(SESSION_HOST_ID_KEY)
}

export function maskSecret(value?: string | null) {
  const text = (value || '').trim()
  if (!text) return ''
  return '********'
}
